import React, { useEffect, useRef, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { MONO_FONT, PAGE_FONT } from "../../components/metrics/metricsUi";
import { purchaseOrderErrorMessage } from "../../utils/purchaseOrderAnalysisPageDefaults";

export const eyebrowSx = (colors) => ({
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: colors.textSecondary,
});

export const tooltipContentStyle = (ui) => ({
  fontSize: 12,
  fontFamily: PAGE_FONT,
  background: ui.colors.bgPaper,
  border: `1px solid ${ui.colors.border}`,
  borderRadius: 8,
  color: ui.colors.textPrimary,
});

/** Fetch with abort + "latest request wins", keyed on `deps`. */
export const useLatestRequest = (load, deps) => {
  const [state, setState] = useState({ data: null, loading: false, error: "" });
  const seq = useRef(0);
  useEffect(() => {
    if (!load) {
      seq.current += 1;
      setState({ data: null, loading: false, error: "" });
      return undefined;
    }
    const current = ++seq.current;
    const controller = new AbortController();
    // Keep the previous data on screen while the next page/filter loads.
    setState((previous) => ({ data: previous.data, loading: true, error: "" }));
    load(controller.signal)
      .then((data) => {
        if (current === seq.current) setState({ data, loading: false, error: "" });
      })
      .catch((err) => {
        if (current !== seq.current || controller.signal.aborted) return;
        setState({ data: null, loading: false, error: purchaseOrderErrorMessage(err) });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
};

export const StatePanel = ({ tone = "info", eyebrow, title, body, ui }) => {
  const Icon = tone === "error" ? ErrorOutlineIcon : InfoOutlinedIcon;
  const accent = tone === "error" ? ui.danger : ui.accent;
  return (
    <Box
      sx={{
        ...ui.card,
        px: 3,
        py: 4.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Icon sx={{ fontSize: 24, color: accent, mb: 1.25 }} />
      {eyebrow ? (
        <Typography sx={{ ...eyebrowSx(ui.colors), color: accent, mb: 0.85 }}>{eyebrow}</Typography>
      ) : null}
      <Typography sx={{ fontSize: 15, fontWeight: 650, color: ui.colors.textPrimary, maxWidth: 560 }}>
        {title}
      </Typography>
      {body ? (
        <Typography sx={{ fontSize: 13, color: ui.colors.textSecondary, maxWidth: 560, mt: 0.85 }}>
          {body}
        </Typography>
      ) : null}
    </Box>
  );
};

export const KpiCard = ({ label, value, hint, color, ui, onClick, active = false, tip }) => (
  <Tooltip title={tip || ""} placement="top" arrow disableHoverListener={!tip}>
    <Box
      {...(onClick ? { component: "button", type: "button", onClick, "aria-pressed": active } : {})}
      sx={{
        ...ui.card,
        borderLeft: `4px solid ${color}`,
        p: "13px 15px 12px",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        fontFamily: "inherit",
        ...(onClick ? { cursor: "pointer", "&:hover": { backgroundColor: ui.surfaceAlt } } : {}),
        ...(active ? { boxShadow: `0 0 0 2px ${color}` } : {}),
      }}
    >
      <Typography sx={{ ...eyebrowSx(ui.colors), lineHeight: 1.35 }}>{label}</Typography>
      <Typography
        sx={{
          fontFamily: MONO_FONT,
          fontSize: 23,
          fontWeight: 650,
          fontVariantNumeric: "tabular-nums",
          color: ui.colors.textPrimary,
          mt: 0.75,
          lineHeight: 1.15,
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: ui.colors.textTertiary, mt: "auto", pt: 0.9, lineHeight: 1.35 }}>
        {hint}
      </Typography>
    </Box>
  </Tooltip>
);

/** A titled card section; `right` holds the section's own controls. */
export const SectionCard = ({ ui, title, subtitle, right, children, sx, id }) => (
  <Box id={id} sx={{ ...ui.card, p: { xs: "14px", md: "16px 18px 18px" }, ...sx }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        mb: 1.75,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: ui.colors.textPrimary }}>{title}</Typography>
        {subtitle ? (
          <Typography sx={{ fontSize: 12.5, color: ui.colors.textSecondary, mt: 0.35, lineHeight: 1.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {right}
    </Box>
    {children}
  </Box>
);

export const Stat = ({ label, value, ui, sub }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={eyebrowSx(ui.colors)}>{label}</Typography>
    <Typography
      sx={{
        fontFamily: MONO_FONT,
        fontSize: 17,
        fontWeight: 650,
        fontVariantNumeric: "tabular-nums",
        color: ui.colors.textPrimary,
        mt: 0.4,
      }}
    >
      {value}
    </Typography>
    {sub ? (
      <Typography sx={{ fontSize: 11, color: ui.colors.textTertiary, mt: 0.25 }}>{sub}</Typography>
    ) : null}
  </Box>
);

/** Coloured dot + text label (identity never by colour alone). */
export const StatusChip = ({ color, label, ui, strong = false }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.6,
      fontSize: 12,
      fontWeight: strong ? 650 : 500,
      color: ui.colors.textPrimary,
      whiteSpace: "nowrap",
    }}
  >
    <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
    {label}
  </Box>
);

/** Received ÷ ordered as a thin bar with the numbers beside it. */
export const ProgressBar = ({ value, total, ui, color }) => {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 48, height: 6, borderRadius: 3, backgroundColor: ui.surfaceAlt, overflow: "hidden" }}>
        <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 3, backgroundColor: color || ui.accent }} />
      </Box>
      <Typography sx={{ fontFamily: MONO_FONT, fontSize: 11.5, color: ui.colors.textSecondary, minWidth: 34, textAlign: "right" }}>
        {Math.round(pct)}%
      </Typography>
    </Box>
  );
};

/** Pill filter button used above tables. */
export const FilterPill = ({ active, onClick, label, badge, color, ui }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={active}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.7,
      fontFamily: "inherit",
      fontSize: 12.5,
      fontWeight: active ? 650 : 500,
      px: 1.25,
      py: 0.6,
      borderRadius: "16px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      border: `1px solid ${active ? color || ui.accent : ui.colors.border}`,
      backgroundColor: active ? ui.accentSoft : ui.colors.bgPaper,
      color: active ? ui.colors.textPrimary : ui.colors.textSecondary,
      "&:hover": { backgroundColor: active ? ui.accentSoft : ui.surfaceAlt },
    }}
  >
    {color ? <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color }} /> : null}
    {label}
    {badge != null ? (
      <Box component="span" sx={{ fontFamily: MONO_FONT, fontSize: 11.5, color: ui.colors.textTertiary }}>
        {badge}
      </Box>
    ) : null}
  </Box>
);
